import React from 'react';
import Tooltip from '@atlaskit/tooltip';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { Size } from '@atlaskit/icon';
import styled from '@emotion/styled';
import { N800 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { ADDITIONAL_INVOICE_RECIPIENTS } from '@css-xp/ppc-constants';

export enum ContactsTooltipType {
  INVOICE = 'invoice',
  CUSTOMER = 'customer',
  BILLING_CONTACT = 'billingAdmin',
  PRIMARY_CUSTOMER_CONTACT = 'primaryContact',
  PRIMARY_QUOTE_CONTACT = 'quotePrimaryContact',
  KEY_CONTACT = 'keyContact',
}

const Container = styled.div`
  display: flex;
  gap: ${token('space.025', '2px')};
  align-items: center;
  span {
    color: ${N800};
  }
`;

export interface ContactsTooltipProps {
  children?: React.ReactNode;
  iconSize?: Size;
  type?: ContactsTooltipType;
}

export const getTooltipContent = (type: ContactsTooltipType) => {
  switch (type) {
    case ContactsTooltipType.INVOICE:
      return `${ADDITIONAL_INVOICE_RECIPIENTS}, in addition to billing admins, receive only invoice notifications.`;
    case ContactsTooltipType.CUSTOMER:
      return 'Your customer contacts are people in contact with you for things like quotes, orders, and billing.';
    case ContactsTooltipType.BILLING_CONTACT:
      return 'Billing admins access and manage payment related activities on behalf of partners.';
    case ContactsTooltipType.PRIMARY_CUSTOMER_CONTACT:
      return 'Your organization’s point of contact who’re in touch with Atlassian billing partners. Also called technical contact.';
    case ContactsTooltipType.PRIMARY_QUOTE_CONTACT:
      return 'Primary contact assigned by customer support while creating the quote.';
    case ContactsTooltipType.KEY_CONTACT:
      return 'Customer point of contacts for your subscriptions. Key contact is your primary billing contact for the billing profile.';
  }
};

export const ContactsTooltip = (props: ContactsTooltipProps) => {
  const { children, type, iconSize = 'medium' } = props;

  if (!type) {
    return <>{children}</>;
  }

  return (
    <Container>
      {children}
      <Tooltip content={getTooltipContent(type)} testId="contact-tooltip" tag="span">
        <InfoIcon label="contacts-tooltip" testId="contacts-info-icon" size={iconSize} />
      </Tooltip>
    </Container>
  );
};
